import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `/contact.php`;

const EMAIL_USER = 'enquiries';
const EMAIL_DOMAIN = 'aftseniorassistedservices.co.uk';
const EMAIL = EMAIL_USER + '@' + EMAIL_DOMAIN;

const SERVICES = [
  'Welfare & Safety Check Visits',
  'Companionship Visits',
  'Medication Reminder Support',
  'Shopping & Errands Assistance',
  'Appointment Transport Support',
  'Hospital Discharge Support',
  'Home Cleaning & Household Support',
  'Pet Walking & Pet Sitting',
  'Social Visits & Community Support',
  'Technology & Digital Support',
  'Home Safety & Organisation Support',
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    selectedServices: [],
    preferredDays: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const already = prev.selectedServices.includes(service);
      return {
        ...prev,
        selectedServices: already
          ? prev.selectedServices.filter(s => s !== service)
          : [...prev.selectedServices, service]
      };
    });
  };

  const removeService = (service) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.filter(s => s !== service)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        serviceType: formData.selectedServices.join(', ')
      };
      await axios.post(`${API}/contact`, payload);
      toast.success('Thank you for contacting us!', {
        description: "We'll get back to you as soon as possible."
      });
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        address: '',
        selectedServices: [],
        preferredDays: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong', {
        description: 'Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">Contact Us</h2>
          <p className="text-base sm:text-lg text-slate-600">
            Ready to learn more about our services? Fill out the form below or give us a call. We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">Get in touch with us</h3>
              <p className="text-slate-600 text-base sm:text-lg mb-8">
                We'd love to hear from you. Whether you have questions about our services or want to arrange support, we're ready to help.
              </p>
            </div>

            <div className="space-y-6">
              {/* Phone */}
              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1">Phone</h4>
                  <a href="tel:07475473449" className="text-teal-600 hover:text-teal-700 text-lg transition-colors font-medium">
                    07475 473 449
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                  <a href={`mailto:${EMAIL}`} className="text-teal-600 hover:text-teal-700 text-sm sm:text-base transition-colors break-all">
                    {EMAIL}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1">Registered Business Address</h4>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Level One, Basecamp Liverpool<br />
                    49 Jamaica Street<br />
                    Liverpool, L1 0AH<br />
                    United Kingdom
                  </p>
                </div>
              </div>

              {/* Service Area */}
              <div className="flex items-start space-x-4 group">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1">Service Area</h4>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Liverpool, Sefton, Wirral, Knowsley,<br />
                    St Helens, Chester, and Ellesmere Port.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-xl mt-8">
              <img
                src="https://images.unsplash.com/photo-1765896387377-e293914d1e69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjYXJlZ2l2ZXIlMjBlbGRlcmx5JTIwaG9tZXxlbnwwfHx8fDE3NzMyMjgwMzV8MA&ixlib=rb-4.1.0&q=85"
                alt="Contact us"
                className="w-full h-64 sm:h-80 object-cover"
              />
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8 lg:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="fullName" className="text-slate-900 font-medium">Full Name *</Label>
                <Input id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Enter your full name" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <Label htmlFor="phone" className="text-slate-900 font-medium">Phone Number *</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Your phone number" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-900 font-medium">Email Address *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Your email address" />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="text-slate-900 font-medium">Address *</Label>
                <Input id="address" name="address" value={formData.address} onChange={handleChange} required className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Your address" />
              </div>

              {/* Multi-select Dropdown */}
              <div ref={dropdownRef}>
                <Label className="text-slate-900 font-medium">
                  Type of Support Needed *{' '}
                  <span className="text-slate-400 font-normal text-xs sm:text-sm">(select all that apply)</span>
                </Label>

                <div
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="mt-2 min-h-10 w-full border border-slate-300 rounded-md px-3 py-2 cursor-pointer flex items-center justify-between hover:border-teal-500 transition-colors"
                >
                  <span className="text-slate-400 text-sm truncate pr-2">
                    {formData.selectedServices.length === 0
                      ? 'Click to select services...'
                      : `${formData.selectedServices.length} service${formData.selectedServices.length > 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </div>

                {dropdownOpen && (
                  <div className="mt-1 w-full border border-slate-200 rounded-md shadow-lg bg-white z-50 max-h-60 overflow-y-auto">
                    {SERVICES.map((service) => (
                      <div
                        key={service}
                        onClick={() => handleServiceToggle(service)}
                        className={`flex items-center space-x-3 px-4 py-3 cursor-pointer transition-colors ${
                          formData.selectedServices.includes(service)
                            ? 'bg-teal-50 text-teal-700'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          formData.selectedServices.includes(service)
                            ? 'bg-teal-600 border-teal-600'
                            : 'border-slate-300'
                        }`}>
                          {formData.selectedServices.includes(service) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm">{service}</span>
                      </div>
                    ))}
                  </div>
                )}

                {formData.selectedServices.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.selectedServices.map((service) => (
                      <span
                        key={service}
                        className="inline-flex items-center bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded-full"
                      >
                        <span className="mr-1">{service}</span>
                        <button type="button" onClick={() => removeService(service)} className="hover:text-teal-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {formData.selectedServices.length === 0 && (
                  <p className="text-slate-400 text-xs mt-1">Please select at least one service</p>
                )}
              </div>

              <div>
                <Label htmlFor="preferredDays" className="text-slate-900 font-medium">Preferred Visit Days</Label>
                <Input id="preferredDays" name="preferredDays" value={formData.preferredDays} onChange={handleChange} className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="e.g., Monday, Wednesday, Friday" />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-900 font-medium">Message</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="mt-2 border-slate-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Tell us more about your needs..." />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white py-6 text-lg font-semibold transition-all duration-300">
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;